import React, { useState } from 'react';
import { useStore, Project } from '../store/useStore';
import { X, Plus, Edit2, Trash2, Calendar, FolderKanban, Loader2 } from 'lucide-react';

interface ProjectManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const ProjectManagementModal: React.FC<ProjectManagementModalProps> = ({ isOpen, onClose }) => {
  const { 
    projects, 
    currentProjectId, 
    setCurrentProjectId, 
    addProject, 
    updateProject, 
    deleteProject, 
    duplicateProjectConfig 
  } = useStore();

  const [isCreating, setIsCreating] = useState(false);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);

  // Form states
  const [name, setName] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [cloneFromProjectId, setCloneFromProjectId] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit states
  const [editName, setEditName] = useState('');
  const [editStartDate, setEditStartDate] = useState('');
  const [editEndDate, setEditEndDate] = useState('');

  if (!isOpen) return null;

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setIsSubmitting(true);
    try {
      // 1. Create the new project in Supabase
      const newProjectId = await addProject(name, startDate || undefined, endDate || undefined);
      
      // 2. If cloning is requested, duplicate config
      if (cloneFromProjectId && newProjectId) {
        await duplicateProjectConfig(cloneFromProjectId, newProjectId);
      }

      // 3. Set the newly created project as the active one
      if (newProjectId) {
        setCurrentProjectId(newProjectId);
      }

      // Reset form
      setName('');
      setStartDate('');
      setEndDate('');
      setCloneFromProjectId('');
      setIsCreating(false);
    } catch (err) {
      console.error('Error creating project:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEdit = (id: string) => {
    if (!editName.trim()) return;
    updateProject(id, {
      name: editName,
      startDate: editStartDate || undefined,
      endDate: editEndDate || undefined,
    });
    setEditingProjectId(null);
  };

  const startEdit = (project: Project) => {
    setEditingProjectId(project.id);
    setEditName(project.name);
    setEditStartDate(project.startDate || '');
    setEditEndDate(project.endDate || '');
  };

  const handleDelete = (id: string) => {
    if (confirm('Êtes-vous sûr de vouloir supprimer ce projet et toutes les données associées (catégories, points, shifts, etc.) ? Cette action est irréversible.')) {
      deleteProject(id);
      if (currentProjectId === id) {
        const remaining = projects.filter(p => p.id !== id);
        if (remaining.length > 0) {
          setCurrentProjectId(remaining[0].id);
        } else {
          setCurrentProjectId(null);
        }
      }
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0A0B0E]/80 backdrop-blur-md">
      <div className="relative w-full max-w-2xl bg-[#14161B] border border-[#2D3139] rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[85vh]">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#2D3139]">
          <div className="flex items-center gap-2.5">
            <FolderKanban className="w-5 h-5 text-indigo-400" />
            <h2 className="text-lg font-bold text-white">Gestion des Projets</h2>
          </div>
          <button 
            onClick={onClose}
            className="p-1 text-slate-400 hover:text-white rounded-lg hover:bg-[#1A1D23] transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar">
          
          {/* Create Button / Form */}
          {!isCreating ? (
            <button
              onClick={() => setIsCreating(true)}
              className="w-full py-3 px-4 flex items-center justify-center gap-2 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-semibold shadow-lg shadow-indigo-600/10 hover:shadow-indigo-600/20 active:scale-[0.98] transition-all"
            >
              <Plus className="w-4 h-4" />
              Créer un nouveau projet
            </button>
          ) : (
            <form onSubmit={handleCreateSubmit} className="bg-[#1C1F26] border border-[#2D3139] rounded-xl p-4 space-y-4">
              <div className="flex items-center justify-between border-b border-[#2D3139] pb-2">
                <span className="text-sm font-bold text-white">Nouveau Projet</span>
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="text-xs text-slate-400 hover:text-white"
                >
                  Annuler
                </button>
              </div>

              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Nom du projet</label>
                  <input
                    type="text"
                    required
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="ex. Woodstock New Age 2026"
                    className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-indigo-500 transition-colors"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1.5">Date de début</label>
                    <input
                      type="date"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-400 mb-1.5">Date de fin</label>
                    <input
                      type="date"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-400 mb-1.5">Dupliquer la configuration depuis</label>
                  <select
                    value={cloneFromProjectId}
                    onChange={(e) => setCloneFromProjectId(e.target.value)}
                    className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
                  >
                    <option value="">-- Projet Vide (Recommencer de zéro) --</option>
                    {projects.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  <p className="mt-1 text-[10px] text-slate-500 leading-normal">
                    Copie les catégories, points et horaires d'ouverture. Les shifts et consommations ne seront pas copiés.
                  </p>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsCreating(false)}
                  className="px-4 py-2 text-sm font-semibold text-slate-400 hover:text-white rounded-lg"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || !name.trim()}
                  className="px-4 py-2 text-sm font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded-lg disabled:opacity-50 flex items-center gap-2"
                >
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Créer le projet
                </button>
              </div>
            </form>
          )}

          {/* Project List */}
          <div className="space-y-3">
            <h3 className="text-xs font-bold text-slate-500 uppercase tracking-widest">Projets Existants</h3>
            
            {projects.length === 0 ? (
              <div className="text-center py-8 text-slate-500 text-sm">
                Aucun projet créé pour le moment.
              </div>
            ) : (
              <div className="space-y-2">
                {projects.map((p) => {
                  const isActive = p.id === currentProjectId;
                  const isEditing = p.id === editingProjectId;

                  return (
                    <div 
                      key={p.id}
                      className={`flex flex-col p-4 rounded-xl border transition-all ${
                        isActive 
                          ? 'bg-[#1C1F26] border-indigo-500/50 shadow-md shadow-indigo-500/5' 
                          : 'bg-[#14161B] border-[#2D3139] hover:bg-[#1C1F26]/50'
                      }`}
                    >
                      {isEditing ? (
                        <div className="space-y-3">
                          <input
                            type="text"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            className="w-full bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-indigo-500 transition-colors"
                          />
                          <div className="grid grid-cols-2 gap-3">
                            <input
                              type="date"
                              value={editStartDate}
                              onChange={(e) => setEditStartDate(e.target.value)}
                              className="bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500 transition-colors"
                            />
                            <input
                              type="date"
                              value={editEndDate}
                              onChange={(e) => setEditEndDate(e.target.value)}
                              className="bg-[#14161B] border border-[#2D3139] rounded-lg px-3 py-2 text-xs text-white focus:outline-none focus:border-indigo-500 transition-colors"
                            />
                          </div>
                          <div className="flex justify-end gap-2 pt-1">
                            <button
                              onClick={() => setEditingProjectId(null)}
                              className="px-3 py-1.5 text-xs font-semibold text-slate-400 hover:text-white rounded"
                            >
                              Annuler
                            </button>
                            <button
                              onClick={() => handleSaveEdit(p.id)}
                              className="px-3 py-1.5 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-500 rounded"
                            >
                              Enregistrer
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-start justify-between">
                          <div className="space-y-1 pr-4 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="font-semibold text-white truncate">{p.name}</span>
                              {isActive && (
                                <span className="flex-none px-2 py-0.5 text-[10px] font-bold text-emerald-400 bg-emerald-500/10 rounded-full border border-emerald-500/20">
                                  Actif
                                </span>
                              )}
                            </div>
                            
                            <div className="flex items-center gap-4 text-xs text-slate-400">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-3.5 h-3.5 text-slate-500" />
                                {p.startDate ? new Date(p.startDate).toLocaleDateString('fr-FR') : 'Non définie'} - {p.endDate ? new Date(p.endDate).toLocaleDateString('fr-FR') : 'Non définie'}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5">
                            {!isActive && (
                              <button
                                onClick={() => setCurrentProjectId(p.id)}
                                className="px-3 py-1.5 text-xs font-semibold text-indigo-400 hover:text-white hover:bg-indigo-600 rounded-lg border border-indigo-500/30 transition-all"
                              >
                                Activer
                              </button>
                            )}
                            <button
                              onClick={() => startEdit(p)}
                              className="p-1.5 text-slate-400 hover:text-white rounded-lg hover:bg-[#2D3139] transition-colors"
                              title="Modifier"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDelete(p.id)}
                              className="p-1.5 text-slate-400 hover:text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
                              title="Supprimer"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};
